import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { abroadAdminApi } from "../../api/abroadLoans";
import { fieldErrorsOf, pageParams, problemOf } from "../../api/scholarships";
import { closesAtOf, countryName, kigaliDate, opensAtOf } from "../scholarships/model";

// Every abroad loan query starts with ABROAD_KEY, so one invalidation refreshes them all
export const ABROAD_KEY = ["abroad-loans"];
export const callKey = (id) => [...ABROAD_KEY, "call", id];

export const CALL_STATES = [
  { value: "DRAFT", label: "Draft", color: "gray" },
  { value: "PUBLISHED", label: "Published", color: "green" },
  { value: "CANCELLED", label: "Cancelled", color: "red" },
];
export const callStateOf = (value) => CALL_STATES.find((s) => s.value === value) ?? { value, label: value ?? "—", color: "gray" };

// A published call is open between its dates; once it has closed, applications are reviewed
export const callPhaseOf = (call) => {
  if (call.state !== "PUBLISHED") return callStateOf(call.state);
  if (call.open) return { label: "Open", color: "green" };
  return new Date(call.closesAt) > new Date() ? { label: "Not yet open", color: "yellow" } : { label: "Closed", color: "blue" };
};

// "Masters · 24 months", plus the institution and country when the programme names them
export const programmeLabel = (p) =>
  [p.name, p.institutionName, p.countryCode && countryName(p.countryCode)].filter(Boolean).join(" · ");

export const durationLabel = (months) => (months % 12 === 0 ? `${months / 12} year${months === 12 ? "" : "s"}` : `${months} months`);

// One page of the HEC Admin's loan calls
export const useAbroadCalls = (filters) =>
  useQuery({
    queryKey: [...ABROAD_KEY, "calls", filters],
    placeholderData: keepPreviousData,
    queryFn: async () => (await abroadAdminApi.calls(pageParams({ ...filters, kind: "LOAN" }))).data,
  });

export const useAbroadCall = (id) =>
  useQuery({
    queryKey: callKey(id),
    enabled: Boolean(id),
    queryFn: async () => (await abroadAdminApi.call(id)).data,
  });

// After a change: the call as the API returned it, and the list refetched
export const useCallSaved = () => {
  const queryClient = useQueryClient();
  return (call) => {
    queryClient.setQueryData(callKey(call.id), call);
    queryClient.invalidateQueries({ queryKey: [...ABROAD_KEY, "calls"] });
  };
};

// One page of the programme catalogue, searched by name or institution
export const useProgrammes = ({ q = "", page = 0, size = 20 } = {}) =>
  useQuery({
    queryKey: [...ABROAD_KEY, "programmes", q, page, size],
    placeholderData: keepPreviousData,
    queryFn: async () => (await abroadAdminApi.programmes(pageParams({ q, page, size }))).data,
  });

// A call's details as the form edits them; dates are Kigali calendar days
export const EMPTY_DETAILS = { name: "", academicYearId: "", awardQuota: "", opensDate: "", closesDate: "", visibility: "PUBLIC", publicSummary: "", description: "" };

export const detailsFormOf = (call) => ({
  name: call.name ?? "",
  academicYearId: call.academicYearId ?? "",
  awardQuota: call.awardQuota == null ? "" : String(call.awardQuota),
  opensDate: kigaliDate(call.opensAt),
  closesDate: kigaliDate(call.closesAt),
  visibility: call.visibility ?? "PUBLIC",
  publicSummary: call.publicSummary ?? "",
  description: call.description ?? "",
});

// The whole CallDetails the API takes on create and on every change; a loan student names their own country
export const detailsOf = (form) => ({
  name: form.name.trim(),
  description: form.description.trim(),
  publicSummary: form.publicSummary.trim() || null,
  visibility: form.visibility,
  academicYearId: form.academicYearId,
  opensAt: opensAtOf(form.opensDate),
  closesAt: closesAtOf(form.closesDate),
  awardQuota: form.awardQuota === "" ? null : Number(form.awardQuota),
});

export const validateDetails = (form) => {
  const errors = {};
  if (!form.name.trim()) errors.name = "Name is required";
  if (!form.academicYearId) errors.academicYearId = "Academic year is required";
  if (!form.opensDate) errors.opensDate = "Opening date is required";
  if (!form.closesDate) errors.closesDate = "Closing date is required";
  else if (form.opensDate && form.closesDate < form.opensDate) errors.closesDate = "Closing date must be on or after the opening date";
  if (!form.description.trim()) errors.description = "Description is required";
  if (form.awardQuota !== "" && !(Number.isInteger(Number(form.awardQuota)) && Number(form.awardQuota) >= 1)) errors.awardQuota = "Enter a whole number of 1 or more";
  return errors;
};

// Which details may change (AbroadCall.accepts): all in a draft; once published, not the visibility
// or academic year, nor the opening date once open; nothing once cancelled
export const detailEditableIn = (call) => {
  if (!call || call.state === "DRAFT") return () => true;
  if (call.state === "CANCELLED") return () => false;
  const opened = new Date(call.opensAt) <= new Date();
  return (field) => !["visibility", "academicYearId"].includes(field) && !(opened && field === "opensDate");
};

// The API's field errors on the form's fields: a create nests them under `details`
const FORM_FIELD = { opensAt: "opensDate", closesAt: "closesDate" };
export const detailErrorsOf = (error) =>
  Object.fromEntries(
    Object.entries(fieldErrorsOf(problemOf(error))).map(([field, message]) => {
      const name = field.replace(/^details\./, "");
      return [FORM_FIELD[name] ?? name, message];
    })
  );
